import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { HotelPlanCancelPolicy } from "admin/types/hotelPlanCancelPolicy";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { Meta } from "admin/types/meta";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { HotelCancelTypes } from "../../../../constants/hotelCancelTypes";

const key = "hotelPlanCancelPolicy";
const adapter = createEntityAdapter<HotelPlanCancelPolicy.CancelPolicy>();

const initialStateValues: HotelPlanCancelPolicy.HotelPlanCancelPolicyState = {
  hotelPlanCancelPolicyId: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchHotelPlanCancelPolicies = createAsyncThunk<
  {
    hotelPlanCancelPolicies: HotelPlanCancelPolicy.CancelPolicy[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    hotelPlanId: number;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelPlanCancelPolicies`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelPlanId,
      param,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_plans/${hotelPlanId}/cancel_policies.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_plans/${hotelPlanId}/cancel_policies.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      hotelPlanCancelPolicies: result.data.hotelPlanCancelPolicies,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postHotelPlanCancelPolicy = createAsyncThunk<
  {
    hotelPlanCancelPolicy: HotelPlanCancelPolicy.CancelPolicy;
  },
  { context: PageContext.PageContext; hotelPlanId: number; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelPlanCancelPolicy`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelPlanId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_plans/${hotelPlanId}/cancel_policies.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_plans/${hotelPlanId}/cancel_policies.json`;
    const result = await createWrapper().post(url, {
      hotel_plan_cancel_policy: params,
    });
    return {
      hotelPlanCancelPolicy: result.data.hotelPlanCancelPolicy,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelPlanCancelPolicy = createAsyncThunk<
  {
    hotelPlanCancelPolicy: HotelPlanCancelPolicy.CancelPolicy;
  },
  { context: PageContext.PageContext; id: number; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelPlanCancelPolicy`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_plan_cancel_policies/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_plan_cancel_policies/${id}.json`;
    const result = await createWrapper().put(url, {
      hotel_plan_cancel_policy: params,
    });
    return {
      hotelPlanCancelPolicy: result.data.hotelPlanCancelPolicy,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const deleteHotelPlanCancelPolicy = createAsyncThunk<
  { id: number },
  { context: PageContext.PageContext; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/deleteHotelPlanCancelPolicy`, async (args, { rejectWithValue }) => {
  try {
    const { context, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_plan_cancel_policies/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_plan_cancel_policies/${id}.json`;
    await createWrapper().delete(url);
    return { id: id };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelPlanCancelPolicySlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setHotelPlanCancelPolicyId: (state, action) => {
      state.hotelPlanCancelPolicyId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelPlanCancelPolicy };
      })
      .addCase(fetchHotelPlanCancelPolicies.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.hotelPlanCancelPolicies);
        state.meta = action.payload.meta;
      })
      .addCase(postHotelPlanCancelPolicy.fulfilled, (state, action) => {
        adapter.addOne(state, action.payload.hotelPlanCancelPolicy);
      })
      .addCase(putHotelPlanCancelPolicy.fulfilled, (state, action) => {
        const { id, ...changes } = action.payload.hotelPlanCancelPolicy;
        adapter.updateOne(state, { id, changes });
      })
      .addCase(deleteHotelPlanCancelPolicy.fulfilled, (state, action) => {
        const { id } = action.payload;
        adapter.removeOne(state, id);
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelPlanCancelPolicyByIdSelector,
  selectIds: hotelPlanCancelPolicyIdsSelector,
  selectEntities: hotelPlanCancelPolicyEntitiesSelector,
  selectAll: hotelPlanCancelPoliciesSelector,
  selectTotal: totalHotelCancelPoliciesSelector,
} = adapter.getSelectors((state: RootState) => state.hotelPlanCancelPolicy);

const stateSelector = (state: RootState) => state[key];

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const hotelCancelPoliciesMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

/** action export **/
export const { setHotelPlanCancelPolicyId } =
  hotelPlanCancelPolicySlice.actions;

export const convertHotelPlanCancelPolicyFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.no_show) {
    delete params.from_days_ago;
    delete params.to_days_ago;
  }
  delete params.no_show;
  return params;
};

export const convertHotelPlanCancelPolicyToFormValues = (data: any) => {
  const values = { ...data };
  if (values.fromDaysAgo === null && values.toDaysAgo === null) {
    values.no_show = true;
  }
  if (values.cancelType === HotelCancelTypes.fixed) {
    delete values.percent;
    delete values.fee;
  } else if (values.cancelType === HotelCancelTypes.percent) {
    delete values.fee;
    delete values.feeCents;
    delete values.feeCurrency;
  }
  return values;
};
